'use client';

import {ReactNode, useState, useEffect, useRef} from 'react';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger
} from '../tooltip/tooltip';
import {TooltipArrow} from '@radix-ui/react-tooltip';
import {useIsMobile} from '../../hooks/use-mobile';
import {cn} from '@purpose/ui/lib/utils';
import {
  Popover,
  PopoverArrow,
  PopoverContent,
  PopoverTrigger
} from '../popover/popover';
import {PopoverPortal} from '@radix-ui/react-popover';

type WithTooltipProps = {
  children: ReactNode;
  title: string;
  description?: string;
  className?: string;
  disabled?: boolean;
  contentClassName?: string;
  maxWidth?: string;
  side?: 'left' | 'right' | 'top' | 'bottom';
  sideOffset?: number;
  titleClassName?: string;
};

const WithTooltip = ({
  children,
  title,
  description,
  className,
  side = 'bottom',
  sideOffset = 8,
  maxWidth = '400px',
  contentClassName,
  disabled = false,
  titleClassName
}: WithTooltipProps) => {
  const isMobile = useIsMobile();

  if (disabled) return children;
  if (isMobile) {
    return (
      <Popover>
        <PopoverTrigger asChild>
          <div className={className}>{children}</div>
        </PopoverTrigger>

        <PopoverPortal>
          <PopoverContent
            side={side}
            sideOffset={sideOffset}
            align="center"
            avoidCollisions
            collisionPadding={8}
            className={cn(
              'bg-popover text-popover-foreground animate-in fade-in-0 zoom-in-95 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 z-50 overflow-hidden rounded-md border px-3 py-1.5 text-sm shadow-md',
              'bg-primary-110 border-none p-4.5 text-xs text-wrap text-white',
              contentClassName
            )}
            style={{maxWidth}}
          >
            <PopoverArrow className="fill-primary-110" />

            <div className="flex flex-col">
              <p className={titleClassName}>{title}</p>
              <p className="font-normal">{description}</p>
            </div>
          </PopoverContent>
        </PopoverPortal>
      </Popover>
    );
  }

  return (
    <TooltipProvider>
      <Tooltip delayDuration={150}>
        <TooltipTrigger className={className} asChild>
          {children}
        </TooltipTrigger>
        <TooltipContent
          side={side}
          sideOffset={sideOffset}
          className={cn(
            'bg-primary-100 border-none p-4.5 text-sm text-wrap text-white',
            contentClassName
          )}
          style={{maxWidth}}
          avoidCollisions
          collisionPadding={8}
        >
          <TooltipArrow className="fill-primary-100" />
          <div className="flex flex-col">
            <p className={cn('whitespace-pre-line', titleClassName)}>{title}</p>
            {description && (
              <p className="text-secondary-60 font-normal">{description}</p>
            )}
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

export {WithTooltip};
